import React from 'react'
import { useUser } from '../UserContext'
import { LogOut, Mail, MapPin, Phone, Calendar, User as UserIcon, Image as ImageIcon } from 'lucide-react'

function formatDate(ts) {
  if (!ts) return '—'
  try {
    // Supports Firestore Timestamp or ISO string/Date
    const date = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts)
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return '—'
  }
}

function Profile() {
  const { currentUser, userData, logout } = useUser()

  const displayName = userData?.name || currentUser?.displayName || 'Guest'
  const email = userData?.email || currentUser?.email || '—'
  const place = userData?.place || '—'
  const address = userData?.address || userData?.adress || '—'
  const phone = userData?.phone || '—'
  const createdAt = userData?.createdAt || currentUser?.metadata?.creationTime || null
  const photoURL = userData?.photoURL || currentUser?.photoURL || ''

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
            <h1 className="text-2xl sm:text-3xl font-bold">Your Profile</h1>
            <p className="text-blue-100 mt-1">Manage your account information</p>
          </div>

          <div className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="relative">
                {photoURL ? (
                  <img src={photoURL} alt="Profile" className="w-28 h-28 rounded-xl object-cover ring-2 ring-blue-200" />
                ) : (
                  <div className="w-28 h-28 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center ring-2 ring-blue-200">
                    <ImageIcon className="w-10 h-10" />
                  </div>
                )}
              </div>

              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <UserIcon className="w-5 h-5 text-blue-600" /> {displayName}
                </h2>
                <div className="mt-2 text-gray-600 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span>{email}</span>
                </div>
              </div>

              <button
                onClick={logout}
                className="inline-flex items-center gap-2 h-11 px-4 rounded-lg text-white font-semibold bg-gradient-to-r from-rose-500 to-rose-600 shadow-md hover:from-rose-600 hover:to-rose-700 active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
              >
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Name</div>
                <div className="text-gray-800 font-medium">{displayName}</div>
              </div>
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Email</div>
                <div className="text-gray-800 font-medium break-all">{email}</div>
              </div>
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-600" /> Country
                </div>
                <div className="text-gray-800 font-medium">{place}</div>
              </div>
              <div className="border border-gray-200 rounded-xl p-4 sm:col-span-2">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Address</div>
                <div className="text-gray-800 font-medium">{address}</div>
              </div>
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-blue-600" /> Phone
                </div>
                <div className="text-gray-800 font-medium">{phone}</div>
              </div>
              <div className="border border-gray-200 rounded-xl p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-600" /> Created At
                </div>
                <div className="text-gray-800 font-medium">{formatDate(createdAt)}</div>
              </div>
            </div>

            {!currentUser && (
              <div className="mt-8 bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-xl p-4">
                You are not logged in. Please sign up or log in to personalize your profile.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Profile